import type { PendingChatMessage, PendingMessageResult } from '@/typings/pending-message';

const PENDING_MESSAGE_KEY = 'widget_pending_chat_message';

/**
 * Save a pending message to localStorage
 * @param message - The message text to save
 * @returns true if saved successfully, false otherwise
 */
export const savePendingMessage = (message: string): boolean => {
    try {
        const pendingMessage: PendingChatMessage = {
            message: message.trim(),
            timestamp: Date.now(),
        };
        localStorage.setItem(PENDING_MESSAGE_KEY, JSON.stringify(pendingMessage));
        return true;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving pending message to localStorage:', error);
        return false;
    }
};

/**
 * Get pending message from localStorage
 * @returns The pending message result with existence flag and message
 */
export const getPendingMessage = (): PendingMessageResult => {
    try {
        const stored = localStorage.getItem(PENDING_MESSAGE_KEY);
        if (!stored) {
            return {
                exists: false,
                message: null,
            };
        }

        const parsed = JSON.parse(stored) as PendingChatMessage;
        return {
            exists: true,
            message: parsed,
        };
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error getting pending message from localStorage:', error);
        return {
            exists: false,
            message: null,
        };
    }
};

/**
 * Remove pending message from localStorage
 * @returns true if removed successfully, false otherwise
 */
export const removePendingMessage = (): boolean => {
    try {
        localStorage.removeItem(PENDING_MESSAGE_KEY);
        return true;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error removing pending message from localStorage:', error);
        return false;
    }
};

/**
 * Check if a pending message exists without retrieving it
 * @returns true if a pending message exists, false otherwise
 */
export const hasPendingMessage = (): boolean => {
    try {
        return localStorage.getItem(PENDING_MESSAGE_KEY) !== null;
    } catch (error) {
        return false;
    }
};
